import React, { Component } from 'react';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import { debounce, keyBy } from 'lodash';

import {
  Button,
  ButtonToolbar,
  Col,
  Grid,
  Modal,
  Row,
} from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { Form } from '../../common-formsy';
import { FormFieldSets, FormManager, IFieldSetPartial } from '../../../lib/mighty-fields';
import { ILoan, ILoanAccount, IProposedPaymentSplit } from '../../../interfaces';
import { getCurrentDate } from '../../../utils/util';

import PaymentAllocationsTable from './PaymentAllocationsTable';
import { IAllocationDraft, allocationsBalance, toDraft, toPayload } from './allocations';

const { LOAN_PAYMENT_TYPE_BALANCE } = AppConstants;

// Form on the left, allocation on the right, as on the add-loan modal
const COL_WIDTHS = { form: 5, stacked: 12, table: 7 };

// The proposal is re-read on every keystroke in the amount field, so the request is held back until
// typing stops. Long enough not to fire mid-figure, short enough that the table does not feel stale.
const PROPOSAL_DEBOUNCE_MS = 400;

const FIELD_SETS: IFieldSetPartial[] = [
  [
    { field: 'amount', label: 'Amount Received', required: true, type: 'money' },
    { field: 'payment_date', required: true, type: 'date' },
    { field: 'notes', type: 'textDynamic' },
  ],
];

interface IProps {
  loanAccount: ILoanAccount;
  // Only to name each allocation's loan; the payment itself carries ids
  loans: ILoan[];
  onClose: () => void;
  onCreate: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
}

/**
 * Logs a payment against the account's loans.
 *
 * The API proposes how the money should land -- oldest loan first, interest before principal -- and
 * the user confirms or edits it before anything is written. That confirmation step is what M-1007
 * asks for and what the SOW's open item on the waterfall ("automated oldest-first vs manual
 * allocation") was asking about.
 *
 * Covers all six of the SOW's payment types. The two that keep the loan open need nothing beyond
 * the split; the four that close it are the Close box on a row.
 */
@inject('Clients')
@autoBindMethods
@observer
class LogLoanPaymentModal extends Component<IProps> {
  @observable private allocations: IAllocationDraft[] = [];
  // The loans this payment is being logged as the end of. Held here rather than on the rows, which
  // a refetch replaces wholesale: the figures split an amount that has changed, but the decision
  // to close a loan outlives any particular split of it.
  @observable private closingLoanIds: string[] = [];
  @observable private amount = '';
  @observable private paymentDate = getCurrentDate();
  @observable private unallocatedAmount = 0;
  @observable private isProposing = false;
  // Set the moment the amount or date changes, not when the request goes out. The debounce leaves
  // ~400ms in which isProposing is still false and the old rows still sum to an unchanged amount,
  // so without this the save button stays live and can post a split worked out for the old date.
  @observable private isProposalStale = false;

  private formManager: FormManager;
  private fetchProposalSoon = debounce(this.fetchProposal, PROPOSAL_DEBOUNCE_MS);
  // The debounce spaces out when requests start, not when they come back. Without a token, a slow
  // proposal for an amount or date the user has moved past lands on top of a newer one -- and if
  // only the date changed the rows still sum to the payment, so nothing looks wrong and the split
  // saved is the one worked out for the wrong accrual date.
  private proposalRequestId = 0;

  public constructor (props: IProps) {
    super(props);

    this.formManager = new FormManager({
      fieldSets: FIELD_SETS,
      // Payments are usually logged the day they arrive, as on the compliance modal. Without this
      // the form seeds payment_date to '' and overwrites the observable on the first change.
      model: { payment_date: this.paymentDate },
      onChange: this.onFormChange,
      onSave: this.onSave,
      onSuccess: props.onClose,
    });
  }

  private get injected () {
    return this.props as IInjected;
  }

  @computed
  private get loansById () {
    return keyBy(this.props.loans, 'id');
  }

  /**
   * The amount and the date are what the proposal is asked for, so a change to either re-reads it --
   * interest accrues daily, so the same amount splits differently on a different date.
   *
   * Edits the user has made to the figures are dropped when that happens, deliberately: they
   * describe a split of an amount that no longer exists. Close ticks are not held on the rows and
   * so survive it -- see closingLoanIds.
   */
  @action
  private onFormChange (data: { amount?: string, payment_date?: string }) {
    const amount = data.amount || ''
      , paymentDate = data.payment_date || ''
      ;

    if (amount === this.amount && paymentDate === this.paymentDate) { return; }

    this.amount = amount;
    this.paymentDate = paymentDate;
    this.isProposalStale = true;
    this.fetchProposalSoon();
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchProposal () {
    const { Clients } = this.injected
      , { loanAccount } = this.props
      ;

    // The API refuses a non-positive amount and a missing date, so there is nothing to ask for yet.
    if (!Number(this.amount) || !this.paymentDate) {
      // Bumped so a proposal already in flight for the old amount cannot pass the guard below and
      // repopulate the table with rows describing a figure the user has deleted.
      ++this.proposalRequestId;
      this.allocations = [];
      this.unallocatedAmount = 0;
      this.isProposalStale = false;
      return;
    }

    const requestId = ++this.proposalRequestId;

    this.isProposing = true;

    try {
      const split: IProposedPaymentSplit = await Clients.loanAccounts.retrieveAction(
        loanAccount.id,
        'proposed-allocations',
        { params: { amount: this.amount, payment_date: this.paymentDate } },
      );

      if (requestId !== this.proposalRequestId) { return; }

      this.allocations = split.allocations.map(toDraft);
      this.unallocatedAmount = split.unallocated_amount;
    }
    catch (error) {
      if (requestId !== this.proposalRequestId) { return; }

      // Transport has already toasted the failure; swallowing it here avoids a second toast. There
      // is no way to allocate by hand -- rows only ever come from the API -- so the table is left
      // empty and the save button stays down until a later amount or date fetches a proposal.
      // tslint:disable-next-line no-console
      console.error('Error fetching the proposed allocations:', error);
      this.allocations = [];
      this.unallocatedAmount = 0;
    }
    finally {
      // A superseded request must not clear the flags a newer one is still holding.
      if (requestId === this.proposalRequestId) {
        this.isProposing = false;
        this.isProposalStale = false;
      }
    }
  }

  // Keyed by loan rather than by index: a refetch between a row's render and its change event would
  // otherwise have the index address a different loan.
  @action
  private onAllocationChange (loanId: string, field: 'amount_to_interest' | 'amount_to_principal', value: string) {
    const allocation = this.allocations.find(candidate => candidate.loan === loanId);

    if (allocation) { allocation[field] = value; }
  }

  /**
   * Marks one loan as settled for good by this payment. Which of the four closed statuses that
   * leaves it in is the API's answer, worked out from what was recovered, so nothing here asks.
   *
   * Not folded into onAllocationChange: that one carries the money as a string off a number input,
   * and widening it to take a boolean too would put the field name in charge of the value's type.
   */
  @action
  private onAllocationCloseChange (loanId: string, closesLoan: boolean) {
    this.closingLoanIds = closesLoan
      ? this.closingLoanIds.concat(loanId)
      : this.closingLoanIds.filter(id => id !== loanId);
  }

  @computed
  private get canSave () {
    return !this.isProposalStale && allocationsBalance(this.allocations, this.amount);
  }

  /**
   * Money the account's loans cannot absorb, which is the one failure the proposal reports rather
   * than throwing: there is nowhere to put it. Nothing in the spec covers overpayment and there is
   * no credit balance to hold it, so the amount has to come down.
   */
  private renderUnallocatedWarning () {
    if (!this.unallocatedAmount) { return null; }

    return (
      <Row className='text-danger'>
        This payment is more than the account's loans owe. Reduce it before logging it.
      </Row>
    );
  }

  private async onSave (data: object) {
    // A disabled button does not stop Enter in a text input from submitting the form, so the rule
    // the button expresses is enforced here too rather than left to the API to refuse.
    if (!this.canSave) { return; }

    const { Clients } = this.injected
      , { loanAccount, onCreate } = this.props
      ;

    await Clients.loanPayments.create({
      ...data,
      account: loanAccount.id,
      allocations: toPayload(this.allocations, this.closingLoanIds),
      type: LOAN_PAYMENT_TYPE_BALANCE,
    });

    await onCreate();
  }

  private renderBody () {
    return (
      <Grid className='col-wrapper' fluid>
        <Row>
          <Col className='col-left' lg={COL_WIDTHS.form} md={COL_WIDTHS.form} sm={COL_WIDTHS.stacked} xs={COL_WIDTHS.stacked}>
            <FormFieldSets fieldSets={FIELD_SETS} {...this.formManager.formGroupsProps} />
          </Col>
          <Col className='col-right' lg={COL_WIDTHS.table} md={COL_WIDTHS.table} sm={COL_WIDTHS.stacked} xs={COL_WIDTHS.stacked}>
            <PaymentAllocationsTable
              allocations={this.allocations}
              amount={this.amount}
              closingLoanIds={this.closingLoanIds}
              loansById={this.loansById}
              onChange={this.onAllocationChange}
              onCloseChange={this.onAllocationCloseChange}
            />
          </Col>
        </Row>
      </Grid>
    );
  }

  public render () {
    const { onClose } = this.props;

    return (
      <Modal show onHide={onClose} className='modal-log-loan-payment right side-modal large two-col'>
        <Form {...this.formManager.formProps}>
          <Modal.Header closeButton>
            <Modal.Title>Log Payment</Modal.Title>
          </Modal.Header>

          <Modal.Body>{this.renderBody()}</Modal.Body>

          <Modal.Footer>
            {this.formManager.errorMessages.map(error => (
              <Row className='text-danger' key={error.field}>
                <strong>{error.field}</strong> - {error.message}
              </Row>
            ))}

            {this.renderUnallocatedWarning()}

            <ButtonToolbar className='pull-right'>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                bsStyle='primary'
                disabled={this.isProposing || !this.canSave || this.formManager.submitDisabled()}
                type='submit'
              >
                {this.formManager.isSubmitting ? 'Saving...' : 'Log payment'}
              </Button>
            </ButtonToolbar>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}

export default LogLoanPaymentModal;
